import React from 'react';
import type { MessageButton } from '../../../../types/chat-message';
import { ReportActionButtons } from './ReportActionButtons';
import { ReportTitle } from './ReportTitle';
import './RevenueReport.scss';

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('vi-VN').format(num);
};

const formatCurrency = (amount: number): string =>
  `${new Intl.NumberFormat('vi-VN').format(amount)}đ`;

const formatQuantity = (quantity?: number): string => {
  const val = quantity ?? 0;
  const intPart = Math.floor(val);
  const frac = val % 1 !== 0 ? `,${Math.round((val % 1) * 100).toString().padStart(2, '0')}` : '';
  return `${formatNumber(intPart)}${frac} sản phẩm`;
};

const formatProductCount = (count?: number): string =>
  `${formatNumber(count ?? 0)} sản phẩm`;

export interface StockReportData {
  date?: string;
  reportType?: string;
  totalQuantity?: number;
  totalAmount?: number;
  totalOutOfStockQuantity?: number;
  totalCanSellQuantity?: number;
}

interface StockReportProps {
  data?: StockReportData;
  buttons?: MessageButton[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  isLastMessage?: boolean;
  isFromHistory?: boolean;
  text?: string | null;
}

const MetricRow: React.FC<{
  label: string;
  value: number | string;
  valueClassName?: string;
}> = ({ label, value, valueClassName }) => (
  <div className="rr-metric-row">
    <span className="rr-metric-label">{label}</span>
    <span className={`rr-metric-value${valueClassName ? ` ${valueClassName}` : ''}`}>
      {typeof value === 'number' && label.includes('Giá') ? formatCurrency(value) : value}
    </span>
  </div>
);

const StockCard: React.FC<{ data: StockReportData }> = ({ data }) => {
  const { totalQuantity, totalAmount, totalOutOfStockQuantity, totalCanSellQuantity } = data;
  return (
    <div className="rr-card">
      <div className="rr-metrics">
        <MetricRow label="Số lượng" value={formatQuantity(totalQuantity)} />
        <MetricRow label="Giá trị tồn" value={totalAmount ?? 0} />
        <div className="rr-divider" />
        <MetricRow
          label="Đã hết hàng"
          value={formatProductCount(totalOutOfStockQuantity)}
          valueClassName="out-of-stock"
        />
        <MetricRow
          label="Còn bán"
          value={formatProductCount(totalCanSellQuantity)}
          valueClassName="can-sell"
        />
      </div>
    </div>
  );
};

const StockHeader: React.FC<{ text?: string | null; date?: string }> = ({ text, date }) => {
  const title = text || (date ? `Báo cáo kho hôm nay ${date}` : 'Báo cáo kho hàng');
  return (
    <div className="rr-header">
      <ReportTitle text={title} />
    </div>
  );
};

interface StockButtonsProps {
  buttons?: MessageButton[];
  isDisabled: boolean;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
}

const StockButtons: React.FC<StockButtonsProps> = ({ buttons, isDisabled, onButtonClick }) => (
  <ReportActionButtons
    buttons={buttons}
    isDisabled={isDisabled}
    onButtonClick={onButtonClick}
    reportType="Báo cáo kho hàng"
    suggestionTitle="Bạn có muốn xem thời gian khác không?"
  />
);

export const StockReport: React.FC<StockReportProps> = (props) => {
  const { data, buttons, onButtonClick, isLastMessage, isFromHistory, text } = props;
  const isButtonDisabled = !isLastMessage || !!isFromHistory;

  return (
    <div className="revenue-report stock-report">
      <StockHeader text={text} date={data?.date} />
      {data && <StockCard data={data} />}
      <StockButtons buttons={buttons} isDisabled={isButtonDisabled} onButtonClick={onButtonClick} />
    </div>
  );
};
